/**
 * Admin dashboard: REF-themed layout with sidebar and section content.
 */
import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Users,
  MessageSquare,
  FileText,
  RefreshCw,
  Activity,
  Trash2,
} from 'lucide-react'
import { useAuth } from '../../components/auth/AuthProvider'
import { API_BASE_URL } from '../../lib/config'
import AdminSidebar, { type AdminSectionValue } from '../components/AdminSidebar'
import TopBar from '../components/TopBar'
import ProfileModal from '../components/ProfileModal'
import TCModal from '../components/TCModal'
import { UsersManagement } from '../../pages/admin/components/UsersManagement'
import { UserDetailPage } from '../../pages/admin/components/UserDetailPage'
import { AnalyticsDashboard } from '../../pages/admin/components/AnalyticsDashboard'
import { DeletedSessionsSection } from '../../pages/admin/components/DeletedSessionsSection'
import { TokenUsage } from '../../pages/admin/components/TokenUsage'
import { GenerateLoginLinkSection } from '../../pages/admin/components/GenerateLoginLinkSection'
import { ConfigurationsSection } from '../../pages/admin/components/ConfigurationsSection'
import { PromptConfigurationsSection } from '../../pages/admin/components/PromptConfigurationsSection'
import { CreditSystemDashboard } from '../../pages/admin/components/CreditSystemDashboard'
import { ReportsSection } from '../../pages/admin/components/ReportsSection'
import { AdminPanelButton } from '../../pages/admin/components/AdminPanelButton'

interface DashboardStats {
  totalUsers: number
  activeUsers: number
  totalChats: number
  totalDeletedChats: number
  totalMessages: number
  totalDeletedMessages: number
  totalSuggestions: number
  todayUsers: number
  todayChats: number
  todayMessages: number
}

const SECTION_TITLE: Record<AdminSectionValue, string> = {
  analytics: 'Analytics',
  users: 'Users',
  reports: 'Reports',
  'login-link': 'Login link',
  configurations: 'Configurations',
  'prompt-editor': 'Prompt Editor',
  'credit-system': 'Credit System',
  'token-usage': 'Token & cost',
  'deleted-sessions': 'Deleted Sessions',
}

export function AdminDashboardPage() {
  const { user, isAdmin } = useAuth()
  const navigate = useNavigate()
  const { userId } = useParams<{ userId: string }>()
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [activeSection, setActiveSection] = useState<AdminSectionValue>(userId ? 'users' : 'analytics')
  const [showProfile, setShowProfile] = useState(false)
  const [showTC, setShowTC] = useState(false)
  const [stats, setStats] = useState<DashboardStats>({
    totalUsers: 0,
    activeUsers: 0,
    totalChats: 0,
    totalDeletedChats: 0,
    totalMessages: 0,
    totalDeletedMessages: 0,
    totalSuggestions: 0,
    todayUsers: 0,
    todayChats: 0,
    todayMessages: 0,
  })
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    fetchDashboardStats()
  }, [])

  useEffect(() => {
    if (userId) setActiveSection('users')
  }, [userId])

  const fetchDashboardStats = async () => {
    setLoading(true)
    try {
      const response = await fetch(`${API_BASE_URL}admin/stats`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      })
      if (response.ok) {
        const data = await response.json()
        setStats(data)
      }
    } catch (error) {
      console.error('Error fetching dashboard stats:', error)
    } finally {
      setLoading(false)
    }
  }

  const sectionTitle = userId ? 'User detail' : (SECTION_TITLE[activeSection] ?? 'Admin Dashboard')
  const userDisplayName = user?.username || user?.email || 'User'
  const userInitials = userDisplayName
    .trim()
    .split(/\s+/)
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) || 'U'

  const cardStyle = {
    background: 'var(--white)',
    borderRadius: 12,
    border: '1px solid var(--bd)',
    boxShadow: 'var(--sh-sm)',
    padding: 16,
  }

  return (
    <div style={{ display: 'flex', width: '100%', height: '100vh', overflow: 'hidden' }}>
      {showProfile && (
        <ProfileModal
          onClose={() => setShowProfile(false)}
          onOpenGuide={() => {}}
          onOpenTC={() => {
            setShowProfile(false)
            setShowTC(true)
          }}
          userDisplayName={userDisplayName}
          userSubtitle="Admin"
          userInitials={userInitials}
          isAdmin={isAdmin}
          onOpenAdmin={() => {
            setShowProfile(false)
            navigate('/admin')
          }}
        />
      )}
      {showTC && <TCModal onClose={() => setShowTC(false)} />}
      <AdminSidebar
        open={sidebarOpen}
        onToggle={() => setSidebarOpen((o) => !o)}
        activeSection={activeSection}
        onSelectSection={(s) => {
          setActiveSection(s)
          setSidebarOpen(false)
          if (userId) {
            navigate('/admin')
          }
        }}
        onBackToChat={() => navigate('/ochat')}
        userDisplayName={userDisplayName}
        userInitials={userInitials}
        onOpenProfile={() => setShowProfile(true)}
        onOpenTC={() => setShowTC(true)}
      />
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          overflow: 'hidden',
          minWidth: 0,
          marginLeft: sidebarOpen ? 'var(--sb-w)' : 'var(--sb-w-collapsed)',
          transition: 'margin-left .3s cubic-bezier(.16,1,.3,1)',
        }}
      >
        <TopBar title={sectionTitle} />
        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            padding: '22px 22px 22px 52px',
          }}
        >
          {/* Stats grid */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
              gap: 16,
              marginBottom: 20,
            }}
          >
            {[
              { label: 'Total Users', value: stats.totalUsers, sub: `+${stats.todayUsers} today`, icon: Users },
              { label: 'Active Users', value: stats.activeUsers, sub: 'Last 7 days', icon: Activity },
              { label: 'Total Chats', value: stats.totalChats, sub: `+${stats.todayChats} today`, icon: MessageSquare },
              { label: 'Total Messages', value: stats.totalMessages, sub: `+${stats.todayMessages} today`, icon: FileText },
              { label: 'Deleted Sessions', value: stats.totalDeletedChats, sub: 'Deleted by users', icon: Trash2, accent: true },
              { label: 'Regenerated', value: stats.totalDeletedMessages, sub: 'Regenerated by users', icon: FileText, accent: true },
            ].map(({ label, value, sub, icon: Icon, accent }) => (
              <div key={label} style={cardStyle}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--muted)' }}>{label}</span>
                  <Icon size={16} style={{ color: accent ? 'var(--gold)' : 'var(--muted2)' }} />
                </div>
                <div style={{ fontSize: 22, fontWeight: 700, color: accent ? 'var(--gold)' : 'var(--text)' }}>{value}</div>
                <p style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>{sub}</p>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <AdminPanelButton type="button" onClick={fetchDashboardStats} disabled={loading}>
              <RefreshCw size={14} />
              Refresh stats
            </AdminPanelButton>
          </div>
          {/* Section content */}
          <div style={cardStyle}>
            {userId ? (
              <UserDetailPage
                userId={userId}
                onBack={() => navigate('/admin')}
              />
            ) : (
              <>
                {activeSection === 'analytics' && <AnalyticsDashboard stats={stats} />}
                {activeSection === 'users' && <UsersManagement />}
                {activeSection === 'reports' && <ReportsSection />}
                {activeSection === 'login-link' && <GenerateLoginLinkSection />}
                {activeSection === 'configurations' && <ConfigurationsSection />}
                {activeSection === 'prompt-editor' && <PromptConfigurationsSection />}
                {activeSection === 'credit-system' && <CreditSystemDashboard />}
                {activeSection === 'token-usage' && <TokenUsage />}
                {activeSection === 'deleted-sessions' && <DeletedSessionsSection />}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
